import { areaLabel, clock, TONE } from '../lib/store-check.js';

// One store in a Store Check: the picture, the counts, and a status for each
// area an owner has marked trusted. The words come from the server and follow
// the spec: "Nothing flagged" rather than "good", "Cannot see" rather than a
// guess, a plain reason when there is no picture. Nothing here is about a
// person, and nothing asks anyone to act.

function Pill({ tone, children }) {
  const t = TONE[tone] || TONE.unread;
  return (
    <span style={{ background: t.bg, color: t.fg, borderRadius: 999, padding: '2px 9px', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>
      {children}
    </span>
  );
}

function Counts({ counts, approximate }) {
  if (!counts) return null;
  const item = (n, label) => <span><b style={{ fontSize: 16, color: 'var(--ink, #1f2a44)' }}>{n}</b> {label}</span>;
  return (
    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: 12, color: '#616161' }} data-counts>
      {item(counts.customers_in_line, 'in line')}{item(counts.customers_seated, 'seated')}{item(counts.staff_visible, 'staff')}
      {approximate && <span title="This store's one camera is far from the customers, so its counts are less exact" style={{ fontStyle: 'italic' }}>approximate</span>}
    </div>
  );
}

function CameraBlock({ cam, showName }) {
  if (cam.state !== 'picture') {
    return (
      <div style={{ background: '#f5f5f5', borderRadius: 8, padding: '18px 12px', textAlign: 'center', fontSize: 13, color: '#616161' }} data-camera-state={cam.state}>
        {showName && <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>{cam.name}</div>}
        {cam.message}
      </div>
    );
  }
  const r = cam.reading;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }} data-camera-state="picture">
      {cam.still.url
        ? <a href={cam.still.url} target="_blank" rel="noreferrer" title="Open the picture full size">
            <img src={cam.still.url} alt={`${cam.name} at ${clock(cam.still.received_at)}`} loading="lazy"
              style={{ width: '100%', display: 'block', borderRadius: 8, background: '#eee', aspectRatio: `${cam.still.width} / ${cam.still.height}` }} />
          </a>
        : <div style={{ background: '#f5f5f5', borderRadius: 8, padding: 14, fontSize: 12, color: '#757575', textAlign: 'center' }}>This picture was deleted after 30 days. Its reading is kept.</div>}
      <div style={{ fontSize: 11, color: '#9e9e9e' }}>{showName ? `${cam.name} · ` : ''}taken {clock(cam.still.received_at)}</div>
      {r.state === 'done' ? (
        <>
          <Counts counts={r.counts} approximate={r.counts_approximate} />
          {r.areas.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {r.areas.map((a) => (
                <div key={a.area} style={{ display: 'flex', gap: 8, alignItems: 'baseline', fontSize: 12 }} data-area={a.area} data-tone={a.tone}>
                  <span style={{ minWidth: 104, color: '#616161' }}>{areaLabel(a.area)}</span>
                  <span style={{ color: (TONE[a.tone] || TONE.unread).fg, fontWeight: a.tone === 'flagged' ? 600 : 400 }}>
                    {a.text}{a.tone === 'unseen' && a.note ? ` (${a.note})` : ''}
                  </span>
                </div>
              ))}
            </div>
          )}
          {r.picture_only.length > 0 && (
            <div style={{ fontSize: 11, color: '#9e9e9e' }}>
              In the picture, not read by the AI yet: {r.picture_only.map(areaLabel).join(', ').toLowerCase()}.
            </div>
          )}
        </>
      ) : <div style={{ fontSize: 12, color: '#757575' }}>{r.message}</div>}
    </div>
  );
}

export function StoreCheckCard({ store }) {
  const many = store.cameras.length > 1;
  const noPicture = store.cameras.every((c) => c.state !== 'picture');
  return (
    <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 10, minWidth: 0 }} data-store-check={store.name}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
        <div style={{ fontSize: 15, fontWeight: 700 }}>{store.name}</div>
        <Pill tone={store.headline.tone}>{store.headline.text}</Pill>
      </div>
      {store.cameras.map((cam) => <CameraBlock key={cam.camera_id} cam={cam} showName={many} />)}
      {noPicture && (
        <div style={{ fontSize: 11, color: '#9e9e9e' }}>
          {store.last_still_at ? `Last picture from this store: ${clock(store.last_still_at)}.` : 'No picture has arrived from this store yet.'}
        </div>
      )}
    </div>
  );
}
